import React, { useState, useEffect } from 'react';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import dayjs from 'dayjs';
import { StaticDatePicker as MuiStaticDatePicker } from '@mui/x-date-pickers';
import { useTranslate } from '@im/react-localization';
import { DatePickerProvider } from 'src/provider/DatePickerProvider';
import {
  DeatilsResponse,
  useExpirationDateChange,
} from 'src/views/vendorprogram/hooks/deal-details';
import { StyledText } from '../../../../components/StyledTypography';
import Button from '@mui/material/Button';

export function StaticDatePicker(
  params: Readonly<{
    anchorEl: HTMLButtonElement | null;
    countryCode: string;
    data: DeatilsResponse | undefined;
    datePickerType: 'Effective' | 'Expiration' | null;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
    setDatePickerType: React.Dispatch<
      React.SetStateAction<'Effective' | 'Expiration' | null>
    >;
  }>
) {
  const details = params.data;
  const translate = useTranslate();
  const [applyToAllLines, setApplyToAllLines] = useState<boolean>(false);
  const [expiration, setExpiration] = useState<string | null>(null);
  const [effective, setEffective] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const expirationDate = expiration ?? details?.expiration_date ?? '';
  const effectiveDate = effective ?? details?.effective_date ?? '';
  const changeDate = useExpirationDateChange({
    applyToAllLines: applyToAllLines,
    countryCode: params.countryCode,
    dealid: details?.deal_id ?? 0,
    effective_date:
      params.datePickerType === 'Effective'
        ? dayjs(effectiveDate).format('YYYY-MM-DD')
        : null,
    expiration_date:
      params.datePickerType === 'Expiration'
        ? dayjs(expirationDate).format('YYYY-MM-DD')
        : null,
    importreferenceid: details?.importreferenceid ?? 0,
    page: details?.lines_page ?? 0,
    pageSize: details?.lines_page_size ?? 0,
  });

  const handlePopOverClose = () => {
    params.setAnchorEl(null);
    params.setDatePickerType(null);
  };

  const handleSave = () => {
    handlePopOverClose();
    if (errorMessage === '') changeDate.mutate();
  };
  useEffect(() => {
    if (
      params.datePickerType === 'Expiration' &&
      dayjs(expirationDate).isBefore(effectiveDate)
    ) {
      setErrorMessage(translate('VendorProgram.DatePickerDateLessThanMessage'));
    } else if (dayjs(effectiveDate).isAfter(expirationDate)) {
      setErrorMessage(
        translate('VendorProgram.DatePickerDateGreaterThanMessage')
      );
    } else {
      setErrorMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expirationDate, effectiveDate, params.datePickerType]);
  return (
    <Popover
      anchorEl={params.anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      data-testid="gds-vp-dd-ed-popOver"
      onClose={handlePopOverClose}
      open={Boolean(params.anchorEl)}
      sx={(theme) => ({ boxShadow: theme.shadows[1] })}
    >
      <Stack>
        {errorMessage && (
          <StyledText
            sx={(theme: any) => ({
              color: theme.palette.error.main,
              padding: theme.spacing(2),
            })}
          >
            {errorMessage}
          </StyledText>
        )}
        <FormGroup
          sx={{
            '.PrivatePickersSlideTransition-root': {
              minHeight: '230px !important',
            },
          }}
        >
          <DatePickerProvider>
            <MuiStaticDatePicker
              data-testid="gds-vp-dd-ed-datepicker"
              displayStaticWrapperAs="desktop"
              maxDate={
                params.datePickerType === 'Effective'
                  ? expirationDate
                  : undefined
              }
              minDate={
                params.datePickerType === 'Expiration'
                  ? effectiveDate
                  : undefined
              }
              onChange={(newValue) => {
                if (!params.datePickerType) return;
                params.datePickerType === 'Expiration'
                  ? setExpiration(newValue)
                  : setEffective(newValue);
              }}
              renderInput={(params) => <TextField {...params} />}
              value={
                params.datePickerType === 'Expiration'
                  ? expirationDate
                  : effectiveDate
              }
            />
          </DatePickerProvider>
        </FormGroup>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                data-testid="gds-vp-dd-ed-checkBox"
                onChange={(e) => setApplyToAllLines(e.target.checked)}
              />
            }
            label={translate('VendorDeals.DatePickerApplyToAllLines')}
            sx={(theme) => ({ marginLeft: theme.spacing(1.5) })}
          ></FormControlLabel>
        </FormGroup>
        <FormGroup>
          <Button
            disabled={Boolean(errorMessage)}
            onClick={handleSave}
            sx={(theme) => ({
              marginBottom: theme.spacing(2),
              marginLeft: theme.spacing(3),
              maxWidth: '25%',
            })}
            variant="contained"
          >
            {translate('Common.Save')}
          </Button>
        </FormGroup>
      </Stack>
    </Popover>
  );
}
